import { useEffect, useRef, useState, useMemo } from "react";
import mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import { MapStyleSwitcher } from "../mapStyleSwitcher/MapStyleSwitcher";
import useRasterTile from "../../../hooks/useRasterTile";
import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
import "./MapPreview.css";

const getCurrentTimeZone = () => {
  const now = new Date();
  const label = new Intl.DateTimeFormat("en-US", {
    timeZone: "Europe/Paris",
    timeZoneName: "long",
  })
    .formatToParts(now)
    .find((p) => p.type === "timeZoneName").value;

  return label.includes("Summer") || label.includes("GMT+2") ? "UTC+2" : "UTC+1";
};

const getResponsiveZoom = () => {
  const width = window.innerWidth;

  if (width < 768) {
    return { initialZoom: 3.8, minZoom: 3.5 };
  } else if (width < 1024) {
    return { initialZoom: 4.2, minZoom: 4.0 };
  }
  return { initialZoom: 4.6, minZoom: 4.6 };
};

const MapPreview = ({ forecastOffset = 0 }) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);

  const [mapLoaded, setMapLoaded] = useState(false);
  const [zoomConfig, setZoomConfig] = useState(getResponsiveZoom);

  const riskMapCountry = "france";

  const currentTimeZone = useMemo(() => getCurrentTimeZone(), []);

  const [dateString, timeString] = useMemo(() => {
    const now = new Date();
    const franceNow = new Date(
      now.toLocaleString("en-US", { timeZone: "Europe/Paris" })
    );

    franceNow.setHours(franceNow.getHours() + forecastOffset);

    const pad = (n) => n.toString().padStart(2, "0");

    return [
      `${pad(franceNow.getDate())}-${pad(franceNow.getMonth() + 1)}-${franceNow.getFullYear()}`,
      `${pad(franceNow.getHours())}:00`,
    ];
  }, [forecastOffset]);

  // update zoomConfig on resize (WITHOUT recreating map)
  useEffect(() => {
    const handleResize = () => {
      const config = getResponsiveZoom();
      setZoomConfig(config);

      // update live map properties
      if (mapRef.current) {
        mapRef.current.setMinZoom(config.minZoom);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const tileActive = mapLoaded;

  useRasterTile(mapRef, "riskMap", 1, tileActive, {
    country: riskMapCountry,
    date: dateString,
    time: timeString,
    utc: currentTimeZone,
    values: [0, 1, 2, 3],
  });

  // ✅ INIT MAP ONLY ONCE
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

    const { initialZoom, minZoom } = zoomConfig;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: "mapbox://styles/mapbox/satellite-streets-v12",
      center: [3.5, 46.2276],
      zoom: initialZoom,
      minZoom: minZoom,
      maxBounds: [
        [-12.0, 35.0],
        [18.0, 57.0],
      ],
    });

    mapRef.current = map;

    map.on("load", () => setMapLoaded(true));

    map.addControl(new mapboxgl.NavigationControl(), "bottom-right");

    map.addControl(
      new mapboxgl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
        showUserHeading: true,
      }),
      "bottom-right"
    );

    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl,
      placeholder:
        "Rechercher des coordonnées, des régions ou des zones à risque...",
      marker: false,
      countries: "fr",
    });

    map.addControl(geocoder, "top-right");
    map.addControl(new MapStyleSwitcher(), "bottom-right");

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  return (
    <div className="map-preview-wrapper">
      <div ref={mapContainerRef} className="map-preview-container" />
    </div>
  );
};

export default MapPreview;